import { useState } from 'react';
import { useSelector } from 'react-redux';
import { notification } from 'antd';
import { formatFullName, sortByAlphabet } from 'shared/utils';
import { getLang } from 'shared/store/selectors/lang.selector';
import { fetchUsers, updateUser } from 'shared/store/actions/users-management.actions';
import type { UnmatchedProvider } from 'shared/api/quality-metric-rq.service.api';
import { Modal, Select } from 'src/components/design-system';
import { useDispatch } from 'src/store/use-dispatch';
import {
  appendProviderName,
  getProviderLabel,
  getProviderNames,
  type ProviderIdentification,
} from 'src/components/user-details/provider-identification';
import {
  resolveErrorMessage,
  resolveProviderNameError,
} from 'src/components/user-details/resolve-error-message';
import { useQualityMetricsManagementContext } from '../quality-metrics-management.context';
import styles from '../quality-metrics-management.module.scss';

export interface OrgUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  providerIdentifications?: ProviderIdentification[];
}

interface AssignProviderModalProps {
  provider: UnmatchedProvider | null;
  users: OrgUser[] | null;
  onClose: () => void;
}

/**
 * Adds an unmatched provider name to a user picked from the organization's
 * users (searchable), through the same `PUT /v1/user/:id` the users page uses.
 * The name is appended under the row's provider — the user's other names are always kept. The save
 * attributes the orphan records and refreshes the quality-metric queries.
 *
 * @param props.provider - The row being assigned; `null` closes the modal.
 * @param props.users - The organization's users; `null` while they load.
 * @param props.onClose - Called on cancel and after a successful save.
 * @returns The modal.
 */
export function AssignProviderModal({ provider, users, onClose }: AssignProviderModalProps) {
  const { lang } = useQualityMetricsManagementContext();
  const usersLang = useSelector(getLang('USERS_MANAGEMENT'));
  const dispatch = useDispatch();
  const [api, contextHolder] = notification.useNotification();
  const [userId, setUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);

  const name = provider?.externalUserName ?? '';
  const providerLabel = provider ? getProviderLabel(provider.provider) : '';
  const selectedUser = users?.find(({ id }) => id === userId) ?? null;
  const currentNames = provider
    ? getProviderNames(selectedUser?.providerIdentifications, provider.provider)
    : [];
  const options = (users ?? [])
    .map(user => ({ value: user.id, label: formatFullName(user, { withEmail: true }) }))
    .sort((a, b) => sortByAlphabet(a.label, b.label));

  const close = () => {
    setUserId(null);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedUser || !provider) return;
    setSaving(true);
    setError(null);
    // The PUT replaces the whole array, so build it from a fresh read: the loaded list can be
    // stale (another tab, another admin), and a stale base would drop names.
    const fresh = (await dispatch(fetchUsers())) as
      { users?: OrgUser[]; error?: string } | undefined;
    const freshUser = fresh?.users?.find(({ id }) => id === selectedUser.id);
    if (!freshUser) {
      setSaving(false);
      setError(usersLang.ERROR);
      return;
    }
    const res = await dispatch(
      updateUser({
        id: freshUser.id,
        userData: {
          email: [freshUser.email],
          providerIdentifications: appendProviderName(
            freshUser.providerIdentifications,
            provider.externalUserName,
            provider.provider,
          ),
        },
        altOwnerId: undefined,
      }),
    );
    setSaving(false);

    const message =
      resolveProviderNameError(res, usersLang)?.message ?? resolveErrorMessage(res, usersLang);
    if (message) {
      setError(message);
      return;
    }
    api.success({
      message: (lang.ASSIGN_PROVIDER_SUCCESS ?? '')
        .replace('{name}', name)
        .replace('{user}', formatFullName(selectedUser)),
      placement: 'bottomRight',
    });
    close();
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={!!provider}
        title={lang.ASSIGN_PROVIDER_TITLE}
        onSubmit={handleSubmit}
        onCancel={close}
        submitText={lang.ASSIGN_PROVIDER_CONFIRM}
        cancelText={lang.CANCEL}
        submitDisabled={!selectedUser || isSaving}
        loading={isSaving}
        submitButtonType="primary">
        <p className={styles.muted}>
          {(lang.ASSIGN_PROVIDER_TEXT ?? '')
            .replace('{provider}', providerLabel)
            .replace('{name}', name)}
        </p>
        <Select
          label={lang.ASSIGN_PROVIDER_USER}
          placeholder={lang.ASSIGN_PROVIDER_USER_PLACEHOLDER}
          name="userId"
          value={userId ?? undefined}
          options={options}
          loading={users === null}
          notFoundContent={
            <span className={styles.selectEmpty}>
              {users === null ? lang.ASSIGN_PROVIDER_USERS_LOADING : lang.ASSIGN_PROVIDER_NO_USERS}
            </span>
          }
          onChange={(_field, value) => {
            setUserId(typeof value === 'string' ? value : null);
            setError(null);
          }}
        />
        {selectedUser && (
          <p className={styles.muted}>
            {currentNames.length
              ? (lang.ASSIGN_PROVIDER_CURRENT_NAMES ?? '')
                  .replace('{provider}', providerLabel)
                  .replace('{names}', currentNames.join('; '))
              : (lang.ASSIGN_PROVIDER_NO_CURRENT_NAMES ?? '').replace('{provider}', providerLabel)}
          </p>
        )}
        {error && (
          <p className={styles.errorText} role="alert">
            {error}
          </p>
        )}
      </Modal>
    </>
  );
}
